import type { ReactElement } from 'react';
import { ThemeProvider, Toaster } from '@repo/ui-web';
import { buildNavGroups } from './catalog/nav';
import { componentSections, foundationSections, webExclusiveSections } from './catalog/sections';
import { SectionNav } from './components/SectionNav';
import { ThemeToggle } from './components/ThemeToggle';
import { AccordionShowcase } from './sections/components/AccordionShowcase';
import { AlertShowcase } from './sections/components/AlertShowcase';
import { AreaTrendChartShowcase } from './sections/components/AreaTrendChartShowcase';
import { AvatarShowcase } from './sections/components/AvatarShowcase';
import { BadgeShowcase } from './sections/components/BadgeShowcase';
import { BreadcrumbsShowcase } from './sections/components/BreadcrumbsShowcase';
import { ButtonShowcase } from './sections/components/ButtonShowcase';
import { ContextMenuShowcase } from './sections/components/ContextMenuShowcase';
import { CarouselShowcase } from './sections/components/CarouselShowcase';
import { ButtonGroupShowcase } from './sections/components/ButtonGroupShowcase';
import { CardShowcase } from './sections/components/CardShowcase';
import { CheckboxShowcase } from './sections/components/CheckboxShowcase';
import { DatePickerShowcase } from './sections/components/DatePickerShowcase';
import { DataTableShowcase } from './sections/components/DataTableShowcase';
import { DialogShowcase } from './sections/components/DialogShowcase';
import { DividerShowcase } from './sections/components/DividerShowcase';
import { DropdownShowcase } from './sections/components/DropdownShowcase';
import { EmailOTPShowcase } from './sections/components/EmailOTPShowcase';
import { FieldShowcase } from './sections/components/FieldShowcase';
import { FileInputShowcase } from './sections/components/FileInputShowcase';
import { InputShowcase } from './sections/components/InputShowcase';
import { InputOTPShowcase } from './sections/components/InputOTPShowcase';
import { ImageCropperShowcase } from './sections/components/ImageCropperShowcase';
import { LinkShowcase } from './sections/components/LinkShowcase';
import { ListRowShowcase } from './sections/components/ListRowShowcase';
import { LoadingShowcase } from './sections/components/LoadingShowcase';
import { MarkdownRendererShowcase } from './sections/components/MarkdownRendererShowcase';
import { PaginationShowcase } from './sections/components/PaginationShowcase';
import { PasswordInputShowcase } from './sections/components/PasswordInputShowcase';
import { PopoverShowcase } from './sections/components/PopoverShowcase';
import { ProgressShowcase } from './sections/components/ProgressShowcase';
import { RadioShowcase } from './sections/components/RadioShowcase';
import { SelectShowcase } from './sections/components/SelectShowcase';
import { SwitchShowcase } from './sections/components/SwitchShowcase';
import { TabsShowcase } from './sections/components/TabsShowcase';
import { TextareaShowcase } from './sections/components/TextareaShowcase';
import { ToastShowcase } from './sections/components/ToastShowcase';
import { TooltipShowcase } from './sections/components/TooltipShowcase';
import { ColorsShowcase } from './sections/foundations/ColorsShowcase';
import { ElevationShowcase } from './sections/foundations/ElevationShowcase';
import { IconographyShowcase } from './sections/foundations/IconographyShowcase';
import { LayoutShowcase } from './sections/foundations/LayoutShowcase';
import { MotionShowcase } from './sections/foundations/MotionShowcase';
import { RadiusShowcase } from './sections/foundations/RadiusShowcase';
import { SpacingShowcase } from './sections/foundations/SpacingShowcase';
import { TypographyShowcase } from './sections/foundations/TypographyShowcase';

const navGroups = buildNavGroups({ foundationSections, componentSections, webExclusiveSections });
const sectionShowcases: Record<string, () => ReactElement> = {
  colors: ColorsShowcase,
  typography: TypographyShowcase,
  spacing: SpacingShowcase,
  radius: RadiusShowcase,
  elevation: ElevationShowcase,
  layout: LayoutShowcase,
  iconography: IconographyShowcase,
  motion: MotionShowcase,
  buttons: ButtonShowcase,
  'button-groups': ButtonGroupShowcase,
  links: LinkShowcase,
  inputs: InputShowcase,
  'otp-inputs': InputOTPShowcase,
  'email-otp': EmailOTPShowcase,
  fields: FieldShowcase,
  'file-inputs': FileInputShowcase,
  'password-inputs': PasswordInputShowcase,
  textareas: TextareaShowcase,
  selects: SelectShowcase,
  dropdowns: DropdownShowcase,
  checkboxes: CheckboxShowcase,
  radios: RadioShowcase,
  switches: SwitchShowcase,
  cards: CardShowcase,
  alerts: AlertShowcase,
  badges: BadgeShowcase,
  avatars: AvatarShowcase,
  tabs: TabsShowcase,
  dialogs: DialogShowcase,
  toasts: ToastShowcase,
  dividers: DividerShowcase,
  progress: ProgressShowcase,
  'list-rows': ListRowShowcase,
  loading: LoadingShowcase,
  accordions: AccordionShowcase,
  pagination: PaginationShowcase,
  'date-pickers': DatePickerShowcase,
  'data-tables': DataTableShowcase,
  'area-trend-charts': AreaTrendChartShowcase,
  'image-croppers': ImageCropperShowcase,
  'markdown-renderers': MarkdownRendererShowcase,
  carousels: CarouselShowcase,
  tooltips: TooltipShowcase,
  popovers: PopoverShowcase,
  breadcrumbs: BreadcrumbsShowcase,
  'context-menus': ContextMenuShowcase,
};

const placeholder = (
  <div className="mt-4 rounded-2xl border border-dashed border-border/60 bg-muted/30 p-4 text-sm text-muted-foreground">
    Planned for a later phase. Add real examples once the pattern library is ready.
  </div>
);

function SectionArticle({ item }: { item: { id: string; title: string; description: string; wide?: boolean } }) {
  const Showcase = sectionShowcases[item.id as keyof typeof sectionShowcases];
  return (
    <article
      id={item.id}
      className={`scroll-mt-16 rounded-3xl border border-border bg-card/70 p-6 shadow-sm${item.wide ? ' md:col-span-2' : ''}`}
    >
      <h3 className="text-base font-semibold text-foreground">{item.title}</h3>
      <p className="mt-2 text-sm text-muted-foreground">{item.description}</p>
      <div className="mt-4">{Showcase ? <Showcase /> : placeholder}</div>
    </article>
  );
}

export function App() {
  return (
    <ThemeProvider storageKey="design-system.theme" defaultTheme="light">
      <div className="min-h-screen">
        <nav className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-md">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-8 py-2">
            <div className="flex items-center">
              {navGroups.map((group) => {
                const allItems = [...group.items, ...(group.exclusiveItems ?? [])];
                return (
                  <div key={group.title} className="group relative">
                    <a
                      href={`#${group.items[0].id}`}
                      className="rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition hover:bg-muted/50 hover:text-foreground"
                    >
                      {group.title}
                    </a>
                    <div className="invisible absolute left-0 top-full pt-1 opacity-0 transition-all group-hover:visible group-hover:opacity-100">
                      <div className="max-h-64 min-w-48 overflow-y-auto rounded-lg border border-border bg-popover p-1 shadow-lg">
                        {allItems.map((item) => (
                          <a
                            key={item.id}
                            href={`#${item.id}`}
                            className="block rounded-md px-3 py-1.5 text-sm text-popover-foreground transition hover:bg-muted/50"
                          >
                            {item.title}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <ThemeToggle />
          </div>
        </nav>

        <header className="mx-auto max-w-6xl p-8">
          <h1 className="text-4xl font-semibold text-foreground">Design System</h1>
        </header>

        <div className="mx-auto flex max-w-6xl flex-col gap-10 px-8 pb-16 lg:flex-row">
          <aside className="lg:sticky lg:top-14 lg:max-h-[calc(100vh-3.5rem)] lg:w-60 lg:overflow-y-auto">
            <SectionNav groups={navGroups} />
          </aside>

          <main className="flex-1 space-y-12">
            {navGroups.map((group) => (
              <section key={group.title} className="space-y-4">
                <header className="space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    {group.title}
                  </p>
                  <h2 className="text-2xl font-semibold text-foreground">{group.title}</h2>
                </header>

                <div className="grid gap-4 md:grid-cols-2">
                  {group.items.map((item) => (
                    <SectionArticle key={item.id} item={item} />
                  ))}
                </div>

                {group.exclusiveItems && group.exclusiveLabel && (
                  <>
                    <div className="flex items-center gap-3 py-2">
                      <div className="h-px flex-1 bg-border" />
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                        {group.exclusiveLabel}
                      </p>
                      <div className="h-px flex-1 bg-border" />
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      {group.exclusiveItems.map((item) => (
                        <SectionArticle key={item.id} item={item} />
                      ))}
                    </div>
                  </>
                )}
              </section>
            ))}
          </main>
        </div>
      </div>
      <Toaster />
    </ThemeProvider>
  );
}
